import { useScatterplotContext } from 'features/timeSeries/ScatterplotContext'
import { useCallback, useMemo } from 'react'

export interface PenVisibilityItem {
  id: string
  name: string
}

interface PenVisibilityActions {
  /** Selected sensors plus derived values whose dependencies are all live. */
  allItems: PenVisibilityItem[]
  areAllHidden: boolean
  setAllVisibility: (isVisible: boolean) => void
}

export const usePenVisibilityActions = (): PenVisibilityActions => {
  const {
    selectedSensors,
    derivedValues,
    sensorVisibilityConfig,
    setSensorVisibilityConfig,
    activeSentinels,
    setSentinelVisibilityConfig,
  } = useScatterplotContext()

  const allItems = useMemo(() => {
    const items: PenVisibilityItem[] = selectedSensors.map((sensor) => ({
      id: sensor.id,
      name: sensor.data.name,
    }))
    derivedValues.forEach((derived) => {
      const allDepsSelected = derived.data.dependencyIds.every(
        (depId) =>
          depId === 'timestamp' ||
          selectedSensors.some((s) => s.id === depId) ||
          derivedValues.some((dv) => dv.data.dependencyIds.includes(depId))
      )
      if (allDepsSelected) {
        items.push({ id: derived.id, name: derived.data.name })
      }
    })
    return items
  }, [selectedSensors, derivedValues])

  const areAllHidden = useMemo(
    () =>
      allItems.length > 0 &&
      allItems.every((item) => sensorVisibilityConfig[item.id] === false),
    [allItems, sensorVisibilityConfig]
  )

  const setAllVisibility = useCallback(
    (isVisible: boolean) => {
      setSensorVisibilityConfig((prev) => {
        let hasChanges = false
        const nextConfig = { ...prev }
        allItems.forEach((item) => {
          if ((prev[item.id] ?? true) !== isVisible) {
            nextConfig[item.id] = isVisible
            hasChanges = true
          }
        })
        return hasChanges ? nextConfig : prev
      })
      // Show/hide all covers sentinel overlays too.
      setSentinelVisibilityConfig((prev) => {
        let hasChanges = false
        const nextConfig = { ...prev }
        activeSentinels.forEach((sentinel) => {
          if ((prev[sentinel.entryId] ?? true) !== isVisible) {
            nextConfig[sentinel.entryId] = isVisible
            hasChanges = true
          }
        })
        return hasChanges ? nextConfig : prev
      })
    },
    [
      allItems,
      setSensorVisibilityConfig,
      activeSentinels,
      setSentinelVisibilityConfig,
    ]
  )

  return { allItems, areAllHidden, setAllVisibility }
}
